import React, { useState, useRef, useEffect } from "react";
import "../styles/login.scss";
/* The board's own edit glyph, exported from Figma 3015:9110 — a 28px SVG that
   already carries the #f97f0f fill. It replaces a 30px PNG used as a MASK over
   an orange box, which drew a different (thicker) pencil than the design. */
import editPencil from "../../../assets/icons/edit-pencil.svg";
import AuthenticationService from "../../../service/auth/AuthenticationService";
import Validator from "../../../util/Validator";
import UserService from "../service/UserService";
import OrbitStage, { T, rv } from "./OrbitStage";
import EventService from "../../../service/event/EventService";
import APIConfig from "../../../service/network/APIConfig";
import MOBILE from "mobile/mobileConfig";
import { useMobileQuery } from "mobile/useIsMobile";

// EMAIL_OTP only appears when the backend asks for it — see requestOTP's
// mfaStatus branch. Doctors with MFA enforced go MOBILE → OTP → EMAIL_OTP →
// TERMS; everyone else skips straight from OTP to TERMS.
const STEP = {
  MOBILE: "MOBILE",
  OTP: "OTP",
  EMAIL_OTP: "EMAIL_OTP",
  TERMS: "TERMS",
};

const MFA_EMAIL_OTP_REQUIRED = "EMAIL_OTP_REQUIRED";


/*
 * The consent Orbit RECORDS (api-consent-service, consent "orbit_terms"). The
 * server holds the text (seeded with the product copy of 2026-09-11) and its
 * version, and the terms step shows the server's copy, so a record always names
 * exactly what was on screen. There is NO local copy to fall back on: a consent
 * that cannot be loaded or recorded — the service down, or not deployed yet —
 * keeps the clinician on this step to retry; nobody gets in unrecorded.
 */
const ORBIT_CONSENT_KEY = "orbit_terms";
const ORBIT_CONSENT_SOURCE = "orbit_web";
const readJson = (res) =>
  res.text().then((text) => {
    try {
      return text ? JSON.parse(text) : null;
    } catch (e) {
      return null;
    }
  });

/* Content the terms step can show and record against. */
const isUsableConsent = (body) =>
  !!body &&
  Array.isArray(body.clauses) &&
  body.clauses.length > 0 &&
  Array.isArray(body.statements) &&
  body.statements.length > 0 &&
  Number(body.currentVersion) > 0;

const Login = () => {
  const [step, setStep] = useState(STEP.MOBILE);
  const [countryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  /* MOBILE ONLY: the digit fields below ask a phone for its number pad. On
     desktop this is {} and the inputs render exactly as before. The side-
     effect-free query, so the login page gets no mobile root class. */
  const numericPad = useMobileQuery() && MOBILE.loginNumericKeypad;
  const digitsOnly = numericPad ? { inputMode: "numeric", pattern: "[0-9]*" } : {};
  /* The consent shown on the terms step, its load state, and the answers. */
  const [consentContent, setConsentContent] = useState(null);
  const [consentState, setConsentState] = useState("idle"); // idle | loading | ok | error
  const [answers, setAnswers] = useState({});
  const [timer, setTimer] = useState(59);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // MFA. partialToken is deliberately component state, never storage — it is a
  // half-authenticated credential and must not outlive the tab.
  const [partialToken, setPartialToken] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpError, setEmailOtpError] = useState("");

  const timerRef = useRef(null);
  const verifiedData = useRef(null);

  const validator = new Validator();
  const userService = new UserService();
  const authService = new AuthenticationService();

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const formatTimer = (seconds) => `00:${String(seconds).padStart(2, "0")}s`;

  const startTimer = () => {
    setCanResend(false);
    setTimer(59);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    []
  );

  const requestOTP = () => {
    if (!validator.validatePhoneNo(phone)) {
      showToast("Enter a valid phone number!");
      return;
    }
    setLoading(true);
    userService.getOTP(
      countryCode.replace("+", ""),
      phone,
      () => {
        setLoading(false);
        setStep(STEP.OTP);
        startTimer();
      },
      (error) => {
        setLoading(false);
        showToast("Phone number is not whitelisted.");
        EventService.emit("showError", { message: error && error.message });
      }
    );
  };

  const resendOTP = () => {
    userService.getOTP(
      countryCode.replace("+", ""),
      phone,
      () => startTimer(),
      () => showToast("Something went wrong!")
    );
    startTimer();
  };

  const verifyOTP = () => {
    if (!validator.validateOTP(otp)) {
      showToast("Enter a valid OTP!");
      return;
    }
    setLoading(true);
    userService.verifyOTP(
      countryCode,
      phone,
      otp,
      authService.getTenantId(),
      (data) => {
        setLoading(false);
        if (!data) {
          showToast("Enter a valid OTP!");
          return;
        }

        // MFA: the backend has emailed a second code and sent us a challenge,
        // not a session. Every token on this response is an empty string and
        // doctorProfile is null, so it must not be treated as a login.
        if (data.mfaStatus === MFA_EMAIL_OTP_REQUIRED) {
          setPartialToken(data.partialToken || "");
          setMaskedEmail(data.maskedEmail || "");
          setEmailOtp("");
          setEmailOtpError("");
          setStep(STEP.EMAIL_OTP);
          return;
        }

        // Anything else without a token is not a session either. Guard it, or a
        // response shape we don't recognise gets stored with an empty token and
        // ProtectedRoute silently bounces back to /login with nothing shown.
        if (!(data.accessToken && data.accessToken.accessToken)) {
          showToast("Could not sign you in — no session was returned.");
          EventService.emit("showError", {
            message: "Login returned no access token.",
          });
          return;
        }

        verifiedData.current = data;
        setStep(STEP.TERMS);
      },
      (error) => {
        setLoading(false);
        showToast((error && error.message) || "Enter a valid OTP!");
        EventService.emit("showError", { message: error && error.message });
      }
    );
  };

  // MFA step 2: exchange the partialToken plus the emailed code for a real
  // session, then fall into the same terms gate as the non-MFA path.
  const verifyEmailOtp = () => {
    if (!/^\d{6}$/.test(emailOtp)) {
      setEmailOtpError("Enter the 6-digit code from the email");
      return;
    }
    setEmailOtpError("");
    setLoading(true);

    userService.verifyDoctorEmailOtp(
      partialToken,
      emailOtp,
      (data) => {
        setLoading(false);
        if (data && data.accessToken && data.accessToken.accessToken) {
          verifiedData.current = data;
          setPartialToken("");
          setStep(STEP.TERMS);
        } else {
          setEmailOtpError("Verification failed. Try again.");
        }
      },
      (error) => {
        setLoading(false);
        const message = (error && error.message) || "Invalid code";
        if (message === "INVALID_EMAIL_OTP") {
          setEmailOtpError("Wrong code, try again");
        } else if (
          message === "EMAIL_OTP_EXPIRED" ||
          message === "PARTIAL_TOKEN_EXPIRED"
        ) {
          setEmailOtpError("Session expired. Please start again.");
        } else {
          setEmailOtpError(message);
        }
      }
    );
  };

  /* Every statement starts ticked — the step's behaviour before consent was
     recorded; the clinician can untick any of them. */
  const showConsent = (content, state) => {
    setConsentContent(content);
    setAnswers(
      content.statements.reduce((all, st) => {
        all[st.key] = true;
        return all;
      }, {})
    );
    setConsentState(state);
  };

  /* The content read is OPEN on the service, so it needs no token — the
     session does not exist yet at this step. */
  const loadConsentContent = () => {
    setConsentState("loading");
    fetch(APIConfig.phrConsent.content.format(ORBIT_CONSENT_KEY), { method: "GET" })
      .then((res) => readJson(res).then((body) => ({ status: res.status, body })))
      .then(({ status, body }) => {
        if (status === 200 && isUsableConsent(body)) showConsent(body, "ok");
        else setConsentState("error");
      })
      .catch(() => setConsentState("error"));
  };

  useEffect(() => {
    if (step === STEP.TERMS && consentState === "idle") loadConsentContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const activateSession = (data) => {
    authService.activateSession(data.accessToken.accessToken);
    authService.setUserProfile(data);
    authService.setLoggedInCookies();
    window.location.href = "/navigo-module";
  };

  /*
   * The consent is RECORDED before the session starts, and a failed record —
   * any failure, including a service without the route — keeps the clinician
   * on this step (they retry) rather than letting them in unrecorded. Sent with the verify response's own token and headers — NOT
   * through the shared API service, whose headers were built with no session
   * and whose 401 handling logs out.
   */
  const acceptTerms = () => {
    const data = verifiedData.current;
    if (!data) {
      setStep(STEP.OTP);
      return;
    }
    if (consentState !== "ok" || !consentContent) return;
    setLoading(true);
    const headers = {
      client: data.doctorProfile && data.doctorProfile.id > 0 ? "doctor_web" : "bridge_web",
      "Content-Type": "application/json",
      auth_token: data.accessToken.accessToken,
    };
    const phoneNo = data.phoneno || data.phoneNo || phone;
    if (phoneNo) headers.phone_no = String(phoneNo);
    fetch(APIConfig.phrConsent.accept, {
      method: "PUT",
      headers,
      body: JSON.stringify({
        consentKey: consentContent.consentKey || ORBIT_CONSENT_KEY,
        consentVersion: consentContent.currentVersion,
        source: ORBIT_CONSENT_SOURCE,
        statements: consentContent.statements.map((st) => ({ key: st.key, accepted: !!answers[st.key] })),
      }),
    })
      /* The save's own failures are handled HERE (network error → null), apart
         from starting the session below — so a failure after the consent is
         saved is never reported as "couldn't record" (a retry would only add
         a duplicate record). */
      .then(
        (res) => readJson(res).then((body) => ({ status: res.status, body }), () => ({ status: res.status, body: null })),
        () => null
      )
      .then((result) => {
        if (!result) {
          setLoading(false);
          showToast("We couldn’t record your consent. Please try again.");
          return;
        }
        const { status } = result;
        if (status >= 200 && status < 300) {
          try {
            activateSession(data);
          } catch (e) {
            setLoading(false);
            showToast("Your consent was recorded, but we couldn’t start your session. Please sign in again.");
          }
          return;
        }
        setLoading(false);
        if (status === 401 || status === 403) {
          /* The verify response's token has expired (or was refused), so a
             retry with it can never succeed: start the sign-in again. */
          verifiedData.current = null;
          setConsentContent(null);
          setConsentState("idle");
          setAnswers({});
          setOtp("");
          setStep(STEP.MOBILE);
          showToast("Your session expired — please sign in again.");
          return;
        }
        if (status === 409) {
          showToast("The terms were just updated — please review them again.");
          loadConsentContent();
          return;
        }
        showToast("We couldn’t record your consent. Please try again.");
      });
  };

  const editPhone = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setOtp("");
    setStep(STEP.MOBILE);
  };


  /* The Orbit handoff's own flag and chevron, drawn inline. */
  const CountryCode = () => (
    <div className="country-code">
      <svg className="flag" width="24" height="17" viewBox="0 0 24 17" role="img" aria-label="India">
        <rect width="24" height="17" rx="2" fill="#F5F5F5" />
        <rect width="24" height="5.7" rx="2" fill="#FF9933" />
        <rect y="11.3" width="24" height="5.7" rx="2" fill="#138808" />
        <circle cx="12" cy="8.5" r="2.2" fill="none" stroke="#1B3F9B" strokeWidth=".9" />
        <circle cx="12" cy="8.5" r=".5" fill="#1B3F9B" />
      </svg>
      <span className="cc">{countryCode}</span>
      <svg className="chevron" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );

  /* The Orbit sign-in (2026-09-17) — UI only: every step, field, handler and
     rule above is unchanged. OrbitStage draws the scene; this column is the
     form. MOBILE and OTP share the handoff's heading and subheading; the email
     code and terms steps show their own title in the subheading's place. */
  return (
    <OrbitStage toast={toastMessage && <div className="toast-message">{toastMessage}</div>}>
      <div className="orbit-panel login-card">
        <div className="orbit-titlegrp">
          <h1 className="orbit-h">
            <span className="rv rv-line" style={rv(T.headline1)}>Welcome</span>
            <span className="rv rv-line" style={rv(T.headline2)}>to Orbit</span>
          </h1>
          <span className="orbit-rule rv rv-rule" style={rv(T.rule)} />
          {step === STEP.TERMS ? (
            /* The title is part of the content the consent service versions and
               hashes, so it is the service's once loaded (a record names it). */
            <h2 className="orbit-sub terms-title">
              {(consentState === "ok" && consentContent && consentContent.title) || "Terms and conditions"}
            </h2>
          ) : (
            <p className="orbit-sub login-subtitle rv rv-up" style={rv(T.subheading)}>
              {step === STEP.EMAIL_OTP
                ? "Two-factor verification"
                : "The intelligent operating system for modern healthcare."}
            </p>
          )}
        </div>

        {step === STEP.MOBILE && (
          <div className="orbit-formgrp mobile-step">
            <div className="orbit-fieldblk rv rv-up-lg" style={rv(T.field)}>
              <label className="orbit-lab" htmlFor="login-mobile">
                Mobile number<i className="req">*</i>
              </label>
              <div className="field mobile-field">
                <CountryCode />
                <span className="orbit-vr" />
                <input
                  id="login-mobile"
                  {...digitsOnly}
                  type="tel"
                  placeholder="Mobile number"
                  value={phone}
                  maxLength={10}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && requestOTP()}
                />
              </div>
            </div>
            <button
              className="primary-btn rv rv-up-lg"
              style={rv(T.cta)}
              onClick={requestOTP}
              disabled={phone.length !== 10 || loading}
            >
              Request OTP
            </button>
          </div>
        )}

        {step === STEP.OTP && (
          <div className="orbit-formgrp otp-step">
            <div className="orbit-fieldblk">
              <label className="orbit-lab" htmlFor="login-mobile-sent">
                Mobile number<i className="req">*</i>
              </label>
              <div className="field mobile-field disabled">
                <CountryCode />
                <span className="orbit-vr" />
                <input id="login-mobile-sent" type="tel" value={phone} disabled />
                <button
                  className="edit-btn"
                  onClick={editPhone}
                  type="button"
                  aria-label="Edit mobile number"
                >
                  <img src={editPencil} alt="" />
                </button>
              </div>
            </div>

            <div className="orbit-fieldblk">
              <label className="orbit-lab" htmlFor="login-otp">
                OTP<i className="req">*</i>
              </label>
              <div className="field otp-field">
                <input
                  id="login-otp"
                  {...digitsOnly}
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  maxLength={5}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && verifyOTP()}
                />
              </div>
              <p className="resend-line">
                {canResend ? (
                  <>
                    Didn&rsquo;t receive OTP? To receive on WhatsApp click{" "}
                    <span className="resend-link" onClick={resendOTP}>
                      resend
                    </span>
                  </>
                ) : (
                  <>
                    Didn&rsquo;t receive OTP? Request for new OTP on WhatsApp in{" "}
                    <span className="timer">{formatTimer(timer)}</span>
                  </>
                )}
              </p>
            </div>

            <button
              className="primary-btn"
              onClick={verifyOTP}
              disabled={otp.length !== 5 || loading}
            >
              Submit
            </button>
          </div>
        )}

        {step === STEP.EMAIL_OTP && (
          <div className="orbit-formgrp email-otp-step">
            <div className="orbit-fieldblk">
              <label className="orbit-lab" htmlFor="login-email-otp">
                Email OTP<i className="req">*</i>
              </label>
              <div className="field email-otp-field">
                <input
                  id="login-email-otp"
                  {...digitsOnly}
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={emailOtp}
                  maxLength={6}
                  autoFocus
                  onChange={(e) => {
                    setEmailOtp(e.target.value.replace(/\D/g, ""));
                    setEmailOtpError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && verifyEmailOtp()}
                />
              </div>

              <p className="mfa-hint">
                {maskedEmail
                  ? `We sent a 6-digit code to ${maskedEmail}`
                  : "We sent a 6-digit code to your registered email"}
              </p>

              {emailOtpError && <p className="field-error">{emailOtpError}</p>}
            </div>

            <button
              className="primary-btn"
              onClick={verifyEmailOtp}
              disabled={emailOtp.length !== 6 || loading}
            >
              Verify
            </button>
          </div>
        )}

        {step === STEP.TERMS && (
          <div className="orbit-formgrp terms-step">
            <div className="terms-body">
              {consentState === "loading" && <p className="terms-note">Loading the terms…</p>}
              {consentState === "error" && (
                <p className="terms-note is-error">
                  Couldn’t load the terms.{" "}
                  <button type="button" className="terms-retry" onClick={loadConsentContent}>
                    Retry
                  </button>
                </p>
              )}
              {consentContent && consentState === "ok" && (
                <ol className="terms-list">
                  {consentContent.clauses.map((clause, index) => (
                    <li key={index}>
                      {/* The heading is its OWN field on the wire, so bolding it
                          is presentation and nothing more — no pattern-matching
                          over copy this app does not own (api-consent-service
                          holds the text; there is no local fallback). */}
                      {clause.heading ? (
                        <strong className="terms-clause-head">{clause.heading}:</strong>
                      ) : null}
                      {clause.heading ? " " : ""}
                      {clause.body}
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {consentContent && consentState === "ok" && (
              <div className="consent-group">
                {consentContent.statements.map((st) => (
                  <label className="consent-line" key={st.key}>
                    {/* The app's own green checkbox (prescriptionCards.scss's
                        `$rx-green` / .rx-cb), not `accent-color`: the tick's
                        colour is the BROWSER's choice with accent-color, and
                        this one has to be white. The real input stays — hidden,
                        not replaced — so the label, the keyboard and the
                        checked state are still the platform's. */}
                    <input
                      type="checkbox"
                      className="consent-cb-input"
                      checked={!!answers[st.key]}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAnswers((all) => ({ ...all, [st.key]: checked }));
                      }}
                    />
                    <span className="consent-cb" aria-hidden="true">
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4">
                        <path d="M3.5 8.4l3 3 6-6.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span>{st.text}</span>
                  </label>
                ))}
              </div>
            )}

            <button
              className="primary-btn"
              onClick={acceptTerms}
              disabled={
                loading ||
                !consentContent ||
                consentState !== "ok" ||
                !consentContent.statements.every((st) => !st.required || answers[st.key])
              }
            >
              Submit
            </button>
          </div>
        )}
      </div>
    </OrbitStage>
  );
};

export default Login;
